/**
 * Mutation Interceptor - Automatic Event Emission
 *
 * Intercepts ALL GraphQL mutations and automatically emits events to the EventBus.
 * This ensures that:
 * 1. Every database change triggers workflow evaluation (TriggerMatcher)
 * 2. Every database change is considered for notifications (NotificationRouter)
 *
 * This is the "ignorant" system - it doesn't know what each mutation does,
 * it just parses the mutation name and emits the appropriate event.
 *
 * Pattern: create_project → entity.created / project
 *          update_task → entity.updated / task
 *          delete_invoice → entity.deleted / invoice
 */

import type { EngineEvent, EngineEventType } from '../ai/engine/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MutationInterceptorConfig {
  /** Whether the interceptor is active (default: true) */
  enabled: boolean;
  /** Debug logging (default: false) */
  debug: boolean;
  /** Mutations to ignore (e.g., 'saveNotificationSettings') */
  ignoreMutations: string[];
}

type EventEmitFn = (event: EngineEvent) => void;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const DEFAULT_CONFIG: MutationInterceptorConfig = {
  enabled: true,
  debug: false,
  ignoreMutations: [
    'saveNotificationSettings',
    'saveUserPreferences',
    'updateLastSeen',
    'markNotificationRead',
  ],
};

let config: MutationInterceptorConfig = { ...DEFAULT_CONFIG };
let emitFn: EventEmitFn | null = null;

// ---------------------------------------------------------------------------
// Mutation Name Parser
// ---------------------------------------------------------------------------

/**
 * Extract mutation name from a GraphQL mutation string.
 * Handles: mutation CreateProject(...) { createProject(...) { ... } }
 * Returns: 'createProject'
 */
function extractMutationName(mutation: string): string | null {
  // Try to find the actual operation name inside the mutation body
  // Look for: { operationName( or { operationName { or { operationName }
  const bodyMatch = mutation.match(/\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*[\({]/);
  if (bodyMatch) {
    return bodyMatch[1];
  }

  // Fallback: look for mutation keyword
  const mutationMatch = mutation.match(/mutation\s+([a-zA-Z_][a-zA-Z0-9_]*)/i);
  if (mutationMatch) {
    return mutationMatch[1];
  }

  return null;
}

/**
 * Parse a camelCase or snake_case mutation name into event type and entity type.
 *
 * Examples:
 *   createProject → { eventType: 'entity.created', entityType: 'project' }
 *   update_task → { eventType: 'entity.updated', entityType: 'task' }
 *   deleteInvoice → { eventType: 'entity.deleted', entityType: 'invoice' }
 *   addContactEmail → { eventType: 'entity.updated', entityType: 'contact' }
 */
function parseMutationName(mutationName: string): {
  eventType: EngineEventType;
  entityType: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
} | null {
  // Normalize: convert camelCase to snake_case for easier parsing
  const normalized = mutationName
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

  // Split by underscore
  const parts = normalized.split('_');
  if (parts.length < 2) return null;

  const verb = parts[0];
  const entity = parts.slice(1).join('_');

  // Map verb to event type
  let eventType: EngineEventType;
  let action: 'CREATE' | 'UPDATE' | 'DELETE';

  switch (verb) {
    case 'create':
    case 'add':
    case 'insert':
    case 'new':
      eventType = 'entity.created';
      action = 'CREATE';
      break;
    case 'update':
    case 'set':
    case 'change':
    case 'modify':
    case 'edit':
    case 'save':
      eventType = 'entity.updated';
      action = 'UPDATE';
      break;
    case 'delete':
    case 'remove':
    case 'destroy':
      eventType = 'entity.deleted';
      action = 'DELETE';
      break;
    default:
      // Unknown verb - treat as update (safest default)
      eventType = 'entity.updated';
      action = 'UPDATE';
  }

  // Map common entity names to our standard types
  const entityTypeMap: Record<string, string> = {
    'project': 'project',
    'projects': 'project',
    'task': 'task',
    'tasks': 'task',
    'time_entry': 'timeentry',
    'timeentry': 'timeentry',
    'time_entries': 'timeentry',
    'milestone': 'milestone',
    'milestones': 'milestone',
    'contact': 'contact',
    'contacts': 'contact',
    'contact_email': 'contact',
    'contact_phone': 'contact',
    'invoice': 'invoice',
    'invoices': 'invoice',
    'bank_account': 'bankaccount',
    'bankaccount': 'bankaccount',
    'bank_transaction': 'banktransaction',
    'banktransaction': 'banktransaction',
    'calendar_event': 'calendar',
    'calendarevent': 'calendar',
    'event': 'calendar',
    'user': 'user',
    'users': 'user',
    'message': 'message',
    'messages': 'message',
    'email': 'email',
    'emails': 'email',
    'file': 'file',
    'files': 'file',
    'document': 'document',
    'documents': 'document',
    'comment': 'comment',
    'comments': 'comment',
    'note': 'note',
    'notes': 'note',
    'tag': 'tag',
    'tags': 'tag',
    'workflow': 'workflow',
    'workflows': 'workflow',
  };

  const mappedEntity = entityTypeMap[entity] || entity;

  return { eventType, entityType: mappedEntity, action };
}

/**
 * Extract entity ID from mutation result.
 * Looks for common patterns: { id: '...' }, { data: { id: '...' } }
 */
function extractEntityId(result: unknown, mutationName: string): string | null {
  if (!result || typeof result !== 'object') return null;

  const obj = result as Record<string, unknown>;

  // Direct ID field
  if (typeof obj.id === 'string') return obj.id;

  // Nested under mutation name (common pattern)
  const nested = obj[mutationName];
  if (nested && typeof nested === 'object') {
    const nestedObj = nested as Record<string, unknown>;
    if (typeof nestedObj.id === 'string') return nestedObj.id;
  }

  // Search all top-level keys for an object with id
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val && typeof val === 'object') {
      const valObj = val as Record<string, unknown>;
      if (typeof valObj.id === 'string') return valObj.id;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the mutation interceptor.
 * Call this once after the EventBus is ready.
 */
export function initMutationInterceptor(emit: EventEmitFn, options?: Partial<MutationInterceptorConfig>): void {
  emitFn = emit;
  config = { ...DEFAULT_CONFIG, ...options };

  if (config.debug) {
    console.log('[MutationInterceptor] Initialized', config);
  }
}

/**
 * Update interceptor configuration.
 */
export function configureMutationInterceptor(options: Partial<MutationInterceptorConfig>): void {
  config = { ...config, ...options };
}

/**
 * Disable the interceptor temporarily.
 */
export function disableMutationInterceptor(): void {
  config.enabled = false;
}

/**
 * Re-enable the interceptor.
 */
export function enableMutationInterceptor(): void {
  config.enabled = true;
}

/**
 * Process a mutation and emit event if appropriate.
 * This should be called AFTER a successful mutation.
 *
 * @param mutation - The GraphQL mutation string
 * @param variables - The mutation variables
 * @param result - The mutation result
 * @param userId - The user who performed the mutation (optional)
 */
export function processMutation(
  mutation: string,
  variables: Record<string, unknown> | undefined,
  result: unknown,
  userId?: string
): void {
  if (!config.enabled || !emitFn) return;

  // Extract mutation name
  const mutationName = extractMutationName(mutation);
  if (!mutationName) {
    if (config.debug) {
      console.log('[MutationInterceptor] Could not extract mutation name:', mutation.substring(0, 100));
    }
    return;
  }

  // Check ignore list
  if (config.ignoreMutations.includes(mutationName)) {
    if (config.debug) {
      console.log('[MutationInterceptor] Ignoring mutation:', mutationName);
    }
    return;
  }

  // Parse mutation name
  const parsed = parseMutationName(mutationName);
  if (!parsed) {
    if (config.debug) {
      console.log('[MutationInterceptor] Could not parse mutation:', mutationName);
    }
    return;
  }

  // Extract entity ID
  const entityId = extractEntityId(result, mutationName) ||
                   (variables?.id as string) ||
                   `auto-${Date.now()}`;

  // Build event
  const event: EngineEvent = {
    id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: parsed.eventType,
    entityType: parsed.entityType,
    entityId,
    action: parsed.action,
    data: {
      mutation: mutationName,
      variables,
      result,
    },
    timestamp: new Date().toISOString(),
    userId: userId || 'system',
    source: 'graphql',
  };

  if (config.debug) {
    console.log('[MutationInterceptor] Emitting event:', {
      type: event.type,
      entityType: event.entityType,
      entityId: event.entityId,
      mutation: mutationName,
    });
  }

  // Emit to EventBus
  emitFn(event);
}

/**
 * Wrap a mutation function to automatically process results.
 * Use this to wrap graphqlMutation.
 */
export function wrapMutation<T>(
  mutationFn: (mutation: string, variables?: Record<string, unknown>) => Promise<T>,
  getUserId?: () => string | undefined
): (mutation: string, variables?: Record<string, unknown>) => Promise<T> {
  return async (mutation: string, variables?: Record<string, unknown>): Promise<T> => {
    const result = await mutationFn(mutation, variables);

    // Process after successful mutation
    processMutation(mutation, variables, result, getUserId?.());

    return result;
  };
}
